import React from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';

const formatRemaining = (days, seconds, t) => {
  /* ≥ 1 day left → “N Days” */
  if (days > 0) return `${days} ${t('Days')}`;

  /* < 1 day left but still valid → “hh h mm m” or “mm m” */
  if (seconds > 0) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);

    if (h > 0) return `${h} ${t('Hours')} ${m} ${t('Minutes')}`;
    if (m > 0) return `${m} ${t('Minutes')}`;
    return `< 1 ${t('Minute')}`;
  }

  /* already expired – caller will render the button instead */
  return null;
};

const TaxAuthorityValidityCell = ({
  tokenDaysRemaining,
  tokenSecondsRemaining,        //  ← new prop
  t,
  client,
  TaxAuthorityService,
  token,
  handleTaxAuthorityAuth,
  setLoading,
  refreshTokenValidity,
  Constants,
}) => {
  // Check if client is a charity organization
  const isCharityOrganization = client?.caseType === Constants?.CharityOrganization;
  
  // Now, stillValid is true if both are zero or if seconds > 0 or days > 0
  const stillValid =  tokenSecondsRemaining > 0 || tokenDaysRemaining > 0;
  return (
    <div className="client-vat-amount">
      {stillValid ? (
        <>
          <span style={{ marginRight: '8px' }}>{formatRemaining(tokenDaysRemaining, tokenSecondsRemaining, t)}</span>
          <button
            onClick={async () => {
              const { isConfirmed } = await Swal.fire({
                title: t('AreYouSure'),
                text: t('AreYouSureResetTaxAuthorityToken') || 'Reset Tax Authority login for this client? You will need to re‑authenticate.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: t('Yes') || 'Yes',
                cancelButtonText: t('Cancel') || 'Cancel',
                reverseButtons: true
              });
              if (!isConfirmed) return;
              setLoading(true);
              try {
                await TaxAuthorityService.deleteClientToken(client.caseId, token);
                await refreshTokenValidity?.(isCharityOrganization);
              } finally {
                setLoading(false);
              }
            }}
            style={{
              padding: '6px 10px',
              cursor: 'pointer',
              border: '1px solid #d9534f',
              borderRadius: '6px',
              background: '#fff',
              color: '#d9534f',
              fontWeight: 'bold',
              fontSize: 'var(--xtram-panel-cell, 12px)',
              transition: 'all 0.2s ease',
              marginRight: '10px'
            }}
            onMouseOver={e => (e.target.style.background = '#f9e6e6')}
            onMouseOut={e => (e.target.style.background = '#fff')}
          >
            {t('Reset')}
          </button>
        </>
      ) : (
        <button
          onClick={async () => {
            setLoading(true);
            try {
              const scope = isCharityOrganization ? Constants?.TaxAuthorityScopes?.Donations : Constants?.TaxAuthorityScopes?.CreationNumber;
              const tokenData = await TaxAuthorityService.requestTokenClient(
                client.caseId,
                token,
                scope
              );
              if (tokenData?.authUrl) {
                handleTaxAuthorityAuth(tokenData.authUrl, scope);
              }
            } finally {
              setLoading(false);
            }
          }}
          style={{
            padding: '10px 16px',
            cursor: 'pointer',
            border: 'none',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #4CAF50, #2E8B57)',
            color: '#fff',
            fontWeight: 'bold',
            fontSize: 'var(--xtram-text-sm, 14px)',
            transition: 'all 0.3s ease',
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
          onMouseOver={e =>
          (e.target.style.background =
            'linear-gradient(135deg, #2E8B57, #228B22)')}
          onMouseOut={e =>
          (e.target.style.background =
            'linear-gradient(135deg, #4CAF50, #2E8B57)')}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            viewBox="0 0 16 16"
            style={{
              width: 'var(--xtram-panel-icon-sm, 16px)',
              height: 'var(--xtram-panel-icon-sm, 16px)',
            }}
          >
            <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05L7.757 10.4a.75.75 0 0 1-1.08 0l-2-2.25a.75.75 0 1 1 1.08-1.04L7.2 8.92l3.77-3.95z" />
          </svg>
          {t('RequestRenewTaxAuthorityLogin')}
        </button>
      )}
    </div>
  );
};

TaxAuthorityValidityCell.propTypes = {
  tokenDaysRemaining: PropTypes.number.isRequired,
  tokenSecondsRemaining: PropTypes.number.isRequired, //  ← NEW
  t: PropTypes.func.isRequired,
  client: PropTypes.object.isRequired,
  TaxAuthorityService: PropTypes.object.isRequired,
  token: PropTypes.string,
  handleTaxAuthorityAuth: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  refreshTokenValidity: PropTypes.func.isRequired,
  Constants: PropTypes.object.isRequired,
};

export default TaxAuthorityValidityCell;
